// One scope for the whole file: terser keeps top-level names, which would otherwise be global.
(() => {
const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const FEATURE_MAX_H = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--feature-max-h')
) || 620;

const cleanups = new Set();
const onCleanup = (fn) => cleanups.add(fn);
window.addEventListener('pagehide', (e) => {
    // A page entering the back/forward cache keeps its listeners.
    if (e.persisted) return;
    cleanups.forEach((fn) => { try { fn(); } catch (err) {} });
    cleanups.clear();
});

const copyText = (text, done) => {
    const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.className = 'gh-visually-hidden';
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch (err) {}
        ta.remove();
        if (ok) done();
    };
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(done, fallback);
    } else {
        fallback();
    }
};

// A button's children are presentational, so its own text never reaches a screen reader.
const liveRegion = document.querySelector('.gh-article-share, .gh-content pre') ? document.createElement('p') : null;
if (liveRegion) {
    liveRegion.className = 'gh-visually-hidden';
    liveRegion.setAttribute('role', 'status');
    document.body.appendChild(liveRegion);
}
const announce = (text) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    requestAnimationFrame(() => { liveRegion.textContent = text; });
};
const T_COPIED = document.body.dataset.i18nCopied || 'Copied';

(() => {
    const navigation = document.querySelector('.gh-navigation');
    const burger = navigation?.querySelector('.gh-burger');
    if (!navigation || !burger) return;

    const inertTargets = () => {
        const out = [];
        const scan = (parent) => {
            for (const el of parent.children) {
                if (el === navigation || el.contains(navigation)) continue;
                if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(el.tagName)) continue;
                out.push(el);
            }
        };
        scan(document.body);
        const viewport = navigation.parentElement;
        if (viewport && viewport !== document.body) scan(viewport);
        return out;
    };
    let inerted = [];

    let lastFocus = null;
    let closeTimer = null;

    const setBurgerLabel = (open) => {
        const label = open
            ? burger.dataset.labelClose || 'Close menu'
            : burger.dataset.labelOpen || 'Menu';
        if (label) burger.setAttribute('aria-label', label);
    };

    const isOpen = () =>
        navigation.classList.contains('is-open') &&
        !navigation.classList.contains('is-closing');

    const panel = () => navigation.querySelector('.gh-navigation-inner');
    const pillHeight = () =>
        parseFloat(getComputedStyle(navigation).getPropertyValue('--gh-nav-pill-h')) || 56;

    let heightAnim = null;

    const animateHeight = (el, from, to, onDone, hold) => {
        if (!el || prefersReducedMotion() || typeof el.animate !== 'function' || from === to) {
            onDone?.();
            return;
        }
        heightAnim?.cancel();
        el.style.overflow = 'hidden';
        const anim = el.animate(
            [{ height: from + 'px' }, { height: to + 'px' }],
            {
                duration: 260,
                easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
                // A close holds its last frame until finishClose drops the class; an open settles to auto.
                fill: hold ? 'forwards' : 'none'
            }
        );
        heightAnim = anim;
        // Animation events arrive a frame late; by then a newer animation may own the element.
        const current = () => !heightAnim || heightAnim === anim;
        anim.addEventListener('finish', () => {
            if (!current()) return;
            el.style.overflow = '';
            if (!hold) heightAnim = null;
            onDone?.();
        }, { once: true });
        anim.addEventListener('cancel', () => {
            if (current()) el.style.overflow = '';
        }, { once: true });
    };

    const finishClose = () => {
        if (!navigation.classList.contains('is-open')) return;
        clearTimeout(closeTimer);
        closeTimer = null;
        navigation.classList.remove('is-open', 'is-closing');
        heightAnim?.cancel();
        heightAnim = null;
        lastFocus?.focus?.({ preventScroll: true });
    };

    const open = () => {
        if (isOpen()) return;
        clearTimeout(closeTimer);
        closeTimer = null;

        const reopening = navigation.classList.contains('is-closing');
        navigation.classList.remove('is-closing');
        if (!reopening || !lastFocus) lastFocus = document.activeElement;

        const el = panel();
        const from = el ? el.getBoundingClientRect().height : 0;

        // A held close animation pins the height; release it before measuring the target.
        heightAnim?.cancel();
        heightAnim = null;

        navigation.classList.add('is-open');
        burger.setAttribute('aria-expanded', 'true');
        setBurgerLabel(true);
        inerted = inertTargets();
        inerted.forEach((el) => el.setAttribute('inert', ''));

        animateHeight(el, from, el ? el.getBoundingClientRect().height : 0);
    };

    const close = (instant) => {
        if (!isOpen()) return;

        burger.setAttribute('aria-expanded', 'false');
        setBurgerLabel(false);
        inerted.forEach((el) => el.removeAttribute('inert'));
        inerted = [];

        navigation.classList.add('is-closing');

        if (instant) {
            finishClose();
            return;
        }

        animateHeight(panel(), panel()?.getBoundingClientRect().height ?? 0, pillHeight(), finishClose, true);

        // finish never fires in a hidden tab.
        closeTimer = setTimeout(finishClose, 500);
    };

    burger.addEventListener('click', (e) => {
        e.stopPropagation();
        isOpen() ? close() : open();
    });

    document.addEventListener('click', (e) => {
        if (!isOpen()) return;
        if (!navigation.contains(e.target)) close();
    });

    navigation.addEventListener('click', (e) => {
        if (!isOpen() || !e.target.closest('a')) return;
        // Focus follows the link, not back to the burger.
        lastFocus = null;
        close();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) close();
    });

    navigation.addEventListener('keydown', (e) => {
        if (!isOpen() || e.key !== 'Tab') return;
        const focusables = Array.from(
            navigation.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.getClientRects().length > 0);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    });

    const mql = window.matchMedia('(min-width: 768px)');
    // The desktop bar is a different height, so a closing animation would end on the wrong one.
    const onMQ = (e) => {
        if (!e.matches) return;
        close(true);
        finishClose();
    };
    mql.addEventListener('change', onMQ);
    onCleanup(() => {
        mql.removeEventListener('change', onMQ);
        clearTimeout(closeTimer);
    });
})();

(() => {
    const externalize = () => {
        document.querySelectorAll('.gh-content a[href]').forEach((a) => {
            let url;
            try { url = new URL(a.href, location.href); } catch (e) { return; }
            if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
            if (url.host === location.host) return;
            if (a.target === '_blank') return;
            a.target = '_blank';
            const rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
            if (!rel.includes('noopener')) rel.push('noopener');
            if (!rel.includes('noreferrer')) rel.push('noreferrer');
            a.setAttribute('rel', rel.join(' '));
        });
    };
    externalize();
    window.addEventListener('load', externalize, { once: true });
})();

(() => {
    const SIDE = 1024;
    const SELECTOR = '.kg-image-card img, .kg-gallery-image img';
    const CONTAINERS = '.kg-image-card, .kg-gallery-image';

    const check = (img) => {
        if (img.dataset.mockupDone) return;
        const wrap = img.closest(CONTAINERS);
        if (!wrap) return;
        img.dataset.mockupDone = '1';

        const markIfSquare = (w, h) => {
            if (w === SIDE && h === SIDE) wrap.classList.add('is-mockup');
        };

        const aw = parseInt(img.getAttribute('width'), 10);
        const ah = parseInt(img.getAttribute('height'), 10);
        if (aw && ah) markIfSquare(aw, ah);
    };

    document.querySelectorAll(SELECTOR).forEach(check);
})();

(() => {
    const watch = (img) => {
        if (img.dataset.bmWatched) return;
        img.dataset.bmWatched = '1';
        const markLoaded = () => img.classList.add('kg-bookmark-icon--loaded');
        if (img.complete && img.naturalWidth > 0) { markLoaded(); return; }
        img.addEventListener('load', markLoaded, { once: true });
    };
    document.querySelectorAll('.kg-bookmark-icon').forEach(watch);
})();

(() => {
    const tables = document.querySelectorAll('.gh-content > table');
    tables.forEach((table) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'gh-table';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    });
})();

(() => {
    const trigger = document.querySelector('.gh-stories-trigger');
    if (!trigger) return;
    const menu = trigger.parentElement;

    const close = () => trigger.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    });

    document.addEventListener('click', (e) => {
        if (!menu.contains(e.target)) close();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
    });
})();

(() => {
    const btn = document.querySelector('.gh-loadmore-btn');
    if (!btn) return;

    const wrap = btn.closest('.gh-loadmore');
    if (!wrap || !wrap.parentElement) return;

    const timeline = wrap.parentElement.querySelector('.gh-activity-timeline');
    if (!timeline) return;

    const status = wrap.querySelector('.gh-loadmore-status');
    const label = btn.querySelector('.gh-loadmore-label');
    const T_MORE = label.textContent;
    const T_LOADING = wrap.dataset.i18nLoading || 'Loading';
    const T_LOADED = wrap.dataset.i18nLoaded || '% more posts loaded';

    let busy = false;

    btn.addEventListener('click', async (e) => {
        const href = btn.getAttribute('href');
        if (!href) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        if (busy) return;

        const fromKeyboard = e.detail === 0;

        busy = true;
        wrap.classList.add('is-loading');
        btn.setAttribute('aria-disabled', 'true');
        label.textContent = T_LOADING;

        const ctl = new AbortController();
        const killer = setTimeout(() => ctl.abort(), 15000);

        let appended = false;
        let navigating = false;

        try {
            const res = await fetch(href, { headers: { Accept: 'text/html' }, signal: ctl.signal });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

            const incoming = doc.querySelectorAll('.gh-activity-timeline > .gh-activity-item');
            if (!incoming.length) throw new Error('no items in response');

            // A post published since this page loaded shifts every later page by one.
            const key = (li) => li.querySelector('.gh-activity-title-link')?.getAttribute('href');
            const shown = new Set([...timeline.children].map(key));
            const fresh = [...incoming].filter((li) => !key(li) || !shown.has(key(li)));

            const frag = document.createDocumentFragment();
            fresh.forEach((li) => frag.appendChild(document.importNode(li, true)));
            const firstNew = frag.firstElementChild;
            timeline.appendChild(frag);
            appended = true;

            const nextHref =
                doc.querySelector('.gh-loadmore-btn')?.getAttribute('href') ||
                doc.querySelector('link[rel=next]')?.getAttribute('href');

            status.textContent = T_LOADED.replace('%', String(fresh.length));

            if (nextHref) {
                btn.setAttribute('href', nextHref);
            } else {
                wrap.remove();
            }

            if (firstNew) {
                firstNew.setAttribute('tabindex', '-1');
                firstNew.classList.add('gh-focus-target');
                if (fromKeyboard) firstNew.focus({ preventScroll: true });
            }
        } catch (err) {
            if (!appended) {
                navigating = true;
                window.location.href = href;
            }
        } finally {
            clearTimeout(killer);
            busy = false;
            if (!navigating) {
                wrap.classList.remove('is-loading');
                btn.removeAttribute('aria-disabled');
                label.textContent = T_MORE;
            }
        }
    });

    // A failed load navigates away mid-request; a page restored from the back/forward cache keeps that state.
    window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        wrap.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
        label.textContent = T_MORE;
    });
})();

(() => {
    const toTop = document.querySelector('.gh-to-top');
    if (!toTop) return;

    const ring = toTop.querySelector('.gh-to-top-ring-bar');
    const readout = document.getElementById('gh-reading-progress');
    const article = ring ? document.querySelector('.gh-article') : null;
    const hasRing = !!(ring && article);

    toTop.hidden = false;

    const circumference = hasRing ? 2 * Math.PI * ring.r.baseVal.value : 0;
    if (hasRing) {
        ring.style.strokeDasharray = String(circumference);
        ring.style.strokeDashoffset = String(circumference);
    }

    let articleTop = 0;
    let articleHeight = 0;

    const measure = () => {
        if (!hasRing) return;
        articleTop = article.getBoundingClientRect().top + window.scrollY;
        articleHeight = article.offsetHeight;
    };

    let ticking = false;
    let lastStep = -1;
    let lastPct = -1;

    const frame = () => {
        ticking = false;

        const y = window.scrollY;
        const vh = window.innerHeight;

        if (hasRing) {
            const total = Math.max(articleHeight - vh, 1);
            const progress = Math.min(Math.max((y - articleTop) / total, 0), 1);

            const step = Math.round(progress * 200);
            if (step !== lastStep) {
                lastStep = step;
                ring.style.strokeDashoffset = String(circumference * (1 - step / 200));
            }
            if (readout) {
                const pct = Math.round(progress * 100);
                if (pct !== lastPct) {
                    lastPct = pct;
                    readout.setAttribute('aria-valuenow', String(pct));
                }
            }
        }

        toTop.classList.toggle('is-visible', y > vh * 0.6);
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(frame);
    };

    const onResize = () => { measure(); onScroll(); };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    let ro = null;
    if (hasRing && 'ResizeObserver' in window) {
        ro = new ResizeObserver(() => { measure(); onScroll(); });
        ro.observe(article);
    }

    toTop.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    });

    onCleanup(() => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (ro) ro.disconnect();
    });

    measure();
    frame();
})();

(() => {
    const boot = () => {
        let cycle = null;
        let safeHTML = null;
        try {
            cycle = document.querySelector('.gh-hero-cycle');
            if (!cycle) return;

            const palette = (cycle.dataset.palette || 'superposition,entanglement,interference,coherence,bloch,tugui')
                .split(',').map((s) => s.trim()).filter(Boolean);

            if (cycle.dataset.topics) {
                const topics = cycle.dataset.topics.split(',')
                    .map((t) => t.trim()).filter(Boolean);
                if (topics.length) {
                    cycle.textContent = '';
                    topics.forEach((topic, i) => {
                        const span = document.createElement('span');
                        span.textContent = topic;
                        span.style.setProperty('--topic-color', `var(--niche-${palette[i % palette.length]})`);
                        cycle.appendChild(span);
                    });
                }
            }

            const words = [];
            for (const span of cycle.children) {
                if (span.tagName !== 'SPAN') continue;
                const text = (span.textContent || '').trim();
                if (!text) continue;
                words.push({
                    text,
                    color: span.style.getPropertyValue('--topic-color') || '',
                });
            }
            if (words.length < 2) return;

            safeHTML = cycle.innerHTML;
            cycle.textContent = '';

            const target = document.createElement('span');
            target.className = 'gh-hero-cycle-target';
            cycle.appendChild(target);
            cycle.classList.add('is-ready');

            const applyColor = (color) => {
                if (color) cycle.style.setProperty('--topic-color', color);
            };

            let index = 0;
            applyColor(words[0].color);
            target.textContent = words[0].text;

            if (prefersReducedMotion()) return;

            const glyphs = '!<>-_/[]{}=+*^?#01ΛΣΘΨΞΩ▮▯⌬│';
            const randInt = (max) => Math.floor(Math.random() * max);

            const charSpans = [];
            const ensureSpans = (count) => {
                while (charSpans.length < count) {
                    const s = document.createElement('span');
                    target.appendChild(s);
                    charSpans.push(s);
                }
            };

            const renderText = (text) => {
                target.textContent = '';
                charSpans.length = 0;
                for (const ch of text) {
                    const s = document.createElement('span');
                    s.textContent = ch;
                    target.appendChild(s);
                    charSpans.push(s);
                }
            };

            renderText(words[0].text);

            let queue = [];
            let step = 0;
            let raf = null;
            let hold = null;
            let gap = null;
            const DWELL_MS = 2400;
            let lastTick = 0;

            const cancel = () => {
                if (raf) { cancelAnimationFrame(raf); raf = null; }
                if (hold) { clearTimeout(hold); hold = null; }
                if (gap) { clearTimeout(gap); gap = null; }
            };

            const schedule = () => {
                gap = window.setTimeout(() => {
                    gap = null;
                    raf = requestAnimationFrame(tick);
                }, Math.max(0, 30 - (performance.now() - lastTick)));
            };

            const tick = () => {
                raf = null;
                lastTick = performance.now();

                let done = 0;
                ensureSpans(queue.length);

                while (charSpans.length > queue.length) {
                    const s = charSpans.pop();
                    s.remove();
                }

                for (let i = 0; i < queue.length; i++) {
                    const q = queue[i];
                    const span = charSpans[i];
                    if (step >= q.end) {
                        done++;
                        span.textContent = q.to;
                        span.className = '';
                    } else if (step >= q.start) {
                        if (!q.glyph || Math.random() < 0.32) {
                            q.glyph = glyphs.charAt(randInt(glyphs.length));
                        }
                        span.textContent = q.glyph;
                        span.className = 'gh-hero-cycle-glitch';
                    } else {
                        span.textContent = q.from;
                        span.className = '';
                    }
                }

                if (done === queue.length) {
                    cycle.classList.remove('is-scrambling');
                    hold = window.setTimeout(() => { hold = null; next(); }, DWELL_MS);
                } else {
                    step++;
                    schedule();
                }
            };

            const scrambleTo = (newText) => {
                cancel();
                const oldText = charSpans.map((s) => s.textContent).join('');
                const len = Math.max(oldText.length, newText.length);
                queue = [];
                const STAGGER = 1.6;
                const WINDOW  = 6;
                for (let i = 0; i < len; i++) {
                    const baseStart = Math.round(i * STAGGER);
                    const jitter    = randInt(2);
                    const start     = baseStart + jitter;
                    queue.push({
                        from: oldText[i] || '',
                        to: newText[i] || '',
                        start,
                        end: start + WINDOW + randInt(2),
                        glyph: '',
                    });
                }
                step = 0;
                cycle.classList.add('is-scrambling');
                raf = requestAnimationFrame(tick);
            };

            const next = () => {
                index = (index + 1) % words.length;
                applyColor(words[index].color);
                scrambleTo(words[index].text);
            };

            const onVisibility = () => {
                cancel();
                if (document.hidden) return;
                if (cycle.classList.contains('is-scrambling')) {
                    raf = requestAnimationFrame(tick);
                } else {
                    hold = window.setTimeout(() => { hold = null; next(); }, 600);
                }
            };
            document.addEventListener('visibilitychange', onVisibility);
            onCleanup(() => {
                document.removeEventListener('visibilitychange', onVisibility);
                cancel();
            });

            hold = window.setTimeout(() => { hold = null; next(); }, DWELL_MS);
        } catch (e) {
            if (cycle && safeHTML !== null) {
                try {
                    cycle.classList.remove('is-ready', 'is-scrambling');
                    cycle.innerHTML = safeHTML;
                } catch (e2) {}
            }
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();

(() => {
    if (prefersReducedMotion()) return;
    const el = document.querySelector('.gh-navigation-logo-text');
    if (!el) return;
    const text = el.textContent;
    if (!text.trim()) return;

    const frag = document.createDocumentFragment();
    let i = 0;
    for (const ch of text) {
        const s = document.createElement('span');
        s.className = 'gh-logo-char';
        s.textContent = ch;
        s.style.setProperty('--i', i++);
        frag.appendChild(s);
    }
    el.textContent = '';
    el.appendChild(frag);
    el.classList.add('is-wave');
})();

(() => {
    const budget = FEATURE_MAX_H;

    const setAR = (img) => {
        if (img.hasAttribute('width')) return;
        const w = img.naturalWidth, h = img.naturalHeight;
        if (!w || !h) return;
        img.style.setProperty('--feature-ar', w + ' / ' + h);
        const figure = img.closest('.gh-article-image');
        if (figure) figure.style.setProperty('--feature-max-w', Math.round(budget * (w / h)) + 'px');
    };
    document.querySelectorAll('.gh-article .gh-article-image img').forEach((img) => {
        if (img.complete) setAR(img);
        else img.addEventListener('load', () => setAR(img), { once: true });
    });
})();

(() => {
    const container = document.querySelector('.gh-article-share');
    if (!container) return;

    const url = container.getAttribute('data-share-url') || window.location.href;
    const title = container.getAttribute('data-share-title') || document.title;
    const encUrl = encodeURIComponent(url);
    const encTitle = encodeURIComponent(title);

    let copiedTimer = null;

    container.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-share]');
        if (!btn) return;

        const type = btn.getAttribute('data-share');
        const w = 600;
        const h = 540;
        const top = Math.max(0, (window.innerHeight - h) / 2) + window.screenY;
        const left = Math.max(0, (window.innerWidth - w) / 2) + window.screenX;
        const features = `noopener,noreferrer,width=${w},height=${h},top=${top},left=${left}`;

        if (type === 'twitter') {
            window.open(`https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}`, '_blank', features);
        } else if (type === 'linkedin') {
            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, '_blank', features);
        } else if (type === 'copy') {
            copyText(url, () => {
                btn.classList.remove('is-copied');
                // Restart the press animation on a repeated copy.
                void btn.offsetWidth;
                btn.classList.add('is-copied');
                clearTimeout(copiedTimer);
                copiedTimer = setTimeout(() => btn.classList.remove('is-copied'), 1800);
                announce(T_COPIED);
            });
        }
    });
})();

(() => {
    const blocks = document.querySelectorAll('.gh-content > pre, .gh-content > .kg-code-card > pre');
    if (!blocks.length) return;

    const d = document.body.dataset;
    const T_COPY = d.i18nCopy || 'Copy';
    const T_COPY_CODE = d.i18nCopyCode || 'Copy code';

    const ICON_COPY = '<svg class="gh-code-copy-idle" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
    const ICON_DONE = '<svg class="gh-code-copy-done" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

    blocks.forEach((pre) => {
        const wrap = document.createElement('div');
        wrap.className = 'gh-code';
        pre.parentNode.insertBefore(wrap, pre);
        wrap.appendChild(pre);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gh-code-copy';
        btn.setAttribute('aria-label', T_COPY_CODE);
        btn.innerHTML = `${ICON_COPY}${ICON_DONE}<span class="gh-code-copy-label">${T_COPY}</span>`;
        wrap.appendChild(btn);

        const label = btn.querySelector('.gh-code-copy-label');
        let revert = null;

        const flash = () => {
            btn.classList.add('is-copied');
            label.textContent = T_COPIED;
            clearTimeout(revert);
            revert = setTimeout(() => {
                btn.classList.remove('is-copied');
                label.textContent = T_COPY;
            }, 1800);
            announce(T_COPIED);
        };

        btn.addEventListener('click', () => {
            const code = pre.querySelector('code') || pre;
            copyText(code.textContent, flash);
        });
    });
})();
})();
